import React from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Card,
  FileInput,
  Group,
  Image,
  Loader,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCheck, IconPencil, IconTrash, IconX } from "@tabler/icons-react";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { AccessLevelsEnum } from "../../../mc-shared/enums";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import { setProjectNameInRedux, setProjectUseDashboardAsDefault } from "../../actions";
import {
  useDeleteProjectLogoMutation,
  useGetProjectLogoTypeAzureBlobStorageFileQuery,
  useUploadLogoTypeMutation,
} from "../../projectRTK";
import { useChangeProjectDefaultMutation, useFetchLayoutsQuery } from "../projectdashboard/ProjectDashboardRTK";
import { doSaveProjectSettings } from "./restQueries";

const GeneralProjectSettingsPage: React.FC = () => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const { data: allLayouts } = useFetchLayoutsQuery(project._id);
  const [changeProjectDefault] = useChangeProjectDefaultMutation();
  const [nameEditMode, setNameEditMode] = React.useState(false);
  const [projectName, setProjectName] = React.useState(project.maconomy.name);

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    setProjectName(project.maconomy.name);
  }, [project.maconomy.name]);

  const saveName = async () => {
    if (projectName.trim() === "" || projectName === project.maconomy.name) {
      setNameEditMode(false);
      return;
    }

    await saveSettings(projectName, project.useDashboardAsDefaultStartPage);
    setNameEditMode(false);
  };

  const cancelNameEdit = () => {
    setProjectName(project.maconomy.name);
    setNameEditMode(false);
  };

  const saveUseDashboardAsDefault = () => {
    saveSettings(project.maconomy.name, !project.useDashboardAsDefaultStartPage);
  };

  const saveSettings = async (name: string, useDashboardAsDefaultStartPage: boolean) => {
    const updated = await doSaveProjectSettings(project._id, name, useDashboardAsDefaultStartPage);
    dispatch(setProjectNameInRedux(updated.name));
    dispatch(setProjectUseDashboardAsDefault(updated.useDashboardAsDefaultStartPage));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
  };

  const handleChangeProjectDefault = async (newDefaultId: string, prevDefaultId: string) => {
    await changeProjectDefault({ newDefaultId, prevDefaultId, projectId: project._id });
    dispatch(setShowSuccessMessageThunk("Default dashboard lagret"));
  };

  const projectLayouts = allLayouts?.filter((item) => item.access.level === AccessLevelsEnum.PROJECT) ?? [];
  const currentDefaultLayout = allLayouts?.find((item) => item.isProjectDefault === true);

  return (
    <Box h="100%" mah="calc(100vh - 150px)" style={{ overflowY: "auto", minHeight: 0 }}>
      <Stack gap="lg" maw={720} pr="sm" pb="lg">
        <Card withBorder radius="md" p="lg">
          <Stack gap="md">
            <Title order={3}>Prosjekt</Title>

            <Stack gap={4}>
              <Text size="sm" fw={600}>
                Prosjektnavn
              </Text>
              {nameEditMode ? (
                <Group gap="xs" align="center" wrap="nowrap">
                  <TextInput
                    value={projectName}
                    onChange={(e) => setProjectName(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveName();
                      if (e.key === "Escape") cancelNameEdit();
                    }}
                    autoFocus
                    style={{ flex: 1 }}
                  />
                  <Tooltip label="Lagre">
                    <ActionIcon
                      variant="light"
                      disabled={projectName.trim() === "" || projectName === project.maconomy.name}
                      onClick={saveName}
                    >
                      <IconCheck size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Avbryt">
                    <ActionIcon variant="subtle" color="gray" onClick={cancelNameEdit}>
                      <IconX size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              ) : (
                <Group gap="xs">
                  <Text>{project.maconomy.name}</Text>
                  <Tooltip label="Rediger">
                    <ActionIcon variant="subtle" onClick={() => setNameEditMode(true)}>
                      <IconPencil size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              )}
            </Stack>

            <Switch
              label="Bruk dashboard som startside"
              checked={project.useDashboardAsDefaultStartPage}
              onChange={saveUseDashboardAsDefault}
            />
          </Stack>
        </Card>

        <Card withBorder radius="md" p="lg">
          <Stack gap="sm">
            <Title order={4}>Dashboardoppsett</Title>
            <Text size="sm">Velg hvilket oppsett prosjektmedlemmer skal bruke som default:</Text>
            <Select
              maw={320}
              value={currentDefaultLayout?._id ?? null}
              data={projectLayouts.map((item) => ({ value: item._id, label: item.title }))}
              onChange={(newDefaultId) => {
                if (newDefaultId == null || newDefaultId === currentDefaultLayout?._id) {
                  return;
                }

                handleChangeProjectDefault(newDefaultId, currentDefaultLayout?._id);
              }}
            />
            <Text size="sm" c="dimmed" fs="italic">
              Bare oppsett med tilgang satt som "Prosjekt" skal vises
            </Text>
          </Stack>
        </Card>

        <ProjectLogoTypeUpload projectId={project._id} />
      </Stack>
    </Box>
  );
};

export default GeneralProjectSettingsPage;

const ProjectLogoTypeUpload: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [imageRefreshKey, setImageRefreshKey] = React.useState(Date.now());

  const {
    data: logoAzureFile,
    isLoading: loadingLogo,
    isError: errorFetchingLogo,
  } = useGetProjectLogoTypeAzureBlobStorageFileQuery({ projectId });

  const [deleteLogo, { isError: errorDeletingFile, isLoading: loadingDelete }] = useDeleteProjectLogoMutation();
  const [uploadLogo, { isError: errorUploadLogo, isLoading: uploadLoading }] = useUploadLogoTypeMutation();

  const isError = errorDeletingFile || errorFetchingLogo || errorUploadLogo;

  const isLoading = loadingDelete || loadingLogo || uploadLoading;

  const path = `/api/image/${logoAzureFile?.blobName}?key=${imageRefreshKey}`;

  const handleUploadLogo = async (image: File | null) => {
    if (image == null) {
      return;
    }

    await uploadLogo({
      image,
      projectId,
    });
    setImageRefreshKey(Date.now());
  };

  const handleDeleteLogo = () => {
    modals.openConfirmModal({
      title: "Slette bildet?",
      children: <Text size="sm">Vil du slette bildet? Dette kan ikke angres!</Text>,
      labels: { confirm: "Slett", cancel: "Avbryt" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        await deleteLogo({ projectId });
        setImageRefreshKey(Date.now());
      },
    });
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={4}>Logotype</Title>
          {isLoading && <Loader size="sm" />}
        </Group>

        {isError && <Alert color="red">Noe gikk galt. Prøv igjen.</Alert>}

        <FileInput label="Last opp et bilde" accept="image/*" onChange={handleUploadLogo} clearable />

        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text fw={600}>Bilde i bruk:</Text>
            <Tooltip label="Slett bilde">
              <ActionIcon variant="subtle" color="red" onClick={handleDeleteLogo}>
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Paper bg="gray.0" p="md" radius="md" withBorder>
            <Image
              src={path}
              alt="Prosjektlogotype"
              h={200}
              fit="contain"
              fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='200'%3E%3Crect width='100%25' height='100%25' fill='%23f1f3f5'/%3E%3C/svg%3E"
            />
          </Paper>
        </Stack>
      </Stack>
    </Card>
  );
};
